import { effect, inject, Injectable, Injector, signal } from "@angular/core";
import { Overlay, OverlayRef } from "@angular/cdk/overlay";
import { ComponentPortal } from "@angular/cdk/portal";
import { CotixLauncherComponent } from "@featuresAY/cotix/cotix-launcher/cotix-launcher.component";
import { FonctionnaliteFacade } from "@shared-ay/fonctionnalite/fonctionnalite.facade";
import { SiteFonctionnalite } from "@jack-sparrow-libraries/emotion-models";
import { SsoApiV2Service } from "@shared-ay/sso/sso-api-v2.service";
import { DomSanitizer } from "@angular/platform-browser";
import { catchError, map } from "rxjs/operators";
import { environment } from "@env/environment";
import { toSignal } from "@angular/core/rxjs-interop";
import { of } from "rxjs";

@Injectable({
	providedIn: "root",
})
export class CotixLauncherService {
	private readonly COTIX_FEATURE_NAME = "AssistantCotation";

	private readonly overlay = inject(Overlay);
	private readonly injector = inject(Injector);
	private readonly fonctionnaliteFacade = inject(FonctionnaliteFacade);
	private readonly ssoApiV2Service = inject(SsoApiV2Service);
	private readonly sanitizer = inject(DomSanitizer);

	private cotixFabRef: OverlayRef | null = null;

	$cotixState = toSignal(
		this.ssoApiV2Service.getCryptedSsoId().pipe(
			map((ssoId) => {
				return {
					cotixUrl: this.sanitizer.bypassSecurityTrustResourceUrl(`${environment.cotixUrl}/?ssoId=${ssoId}`),
					error: false,
					loading: false,
				};
			}),
			catchError(() => {
				return of({ cotixUrl: null, loading: false, error: true });
			}),
		),
		{ initialValue: { cotixUrl: null, loading: true, error: false } },
	);

	private readonly _$isOpen = signal(false);
	readonly $isOpen = this._$isOpen.asReadonly();

	readonly $userHasFeatureCotixFromNewSiteFonctionnalite = this.fonctionnaliteFacade.hasAnyFonctionnalite(
		this.COTIX_FEATURE_NAME as SiteFonctionnalite,
	);

	/**
	 * Initialise la creation de l'overlay cotix si le client a la feature
	 * */
	initCotix() {
		effect(
			() => {
				if (this.$userHasFeatureCotixFromNewSiteFonctionnalite()) {
					this.createCotixOverlay();
				}
			},
			{ injector: this.injector },
		);
	}

	togglePopup() {
		this._$isOpen.update((oldValue) => !oldValue);
	}

	/*On anticipe des maintenant la possibilité d'ouvrir la popup a distance depuis nimporte quel page*/
	/**
	 * Permet l'ouverture depuis n'importe ou la popup
	 * */
	openPopup() {
		if (!this.$userHasFeatureCotixFromNewSiteFonctionnalite()) return;
		this._$isOpen.set(true);
	}

	closePopup() {
		this._$isOpen.set(false);
	}

	/**
	 * Crée et configure un overlay CDK pour afficher le launcher Cotix.
	 *
	 * Cette méthode gère la création d'un overlay positionné en bas à droite de l'écran,
	 * contenant le composant CotixLauncherComponent. Si un overlay Cotix existe déjà,
	 * il est automatiquement supprimé avant d'en créer un nouveau.
	 *
	 *
	 * @example
	 * ```typescript
	 * // Utilisation basique
	 * const overlayRef = this.createCotixOverlay();
	 *
	 * // Avec gestion de la fermeture
	 * const overlayRef = this.createCotixOverlay();
	 * overlayRef.detach(); // Pour fermer l'overlay
	 * ```
	 *
	 * @returns {OverlayRef} Référence à l'overlay créé, permettant de le contrôler (fermer, repositionner, etc.)
	 *
	 * @throws {Error} Peut lever une erreur si le service Overlay n'est pas correctement injecté
	 *
	 * @see {@link CotixLauncherComponent} Composant affiché dans l'overlay
	 * @see {@link https://material.angular.io/cdk/overlay/overview|Angular CDK Overlay}
	 *
	 * @memberof CotixLauncherService
	 * @public
	 */
	private createCotixOverlay(): OverlayRef {
		if (this.cotixFabRef) {
			this.cotixFabRef.dispose();
		}
		this.cotixFabRef = this.overlay.create({
			/*Position faite comme ca car placé a coté du button intercom */
			positionStrategy: this.overlay.position().global().bottom("20px").right("20px"),
			hasBackdrop: false,
			backdropClass: "cotix-modal-backdrop",
			panelClass: "cotix-modal-panel",
		});
		const cotixFABPortal = new ComponentPortal(CotixLauncherComponent);
		this.cotixFabRef.attach(cotixFABPortal);
		return this.cotixFabRef;
	}
}
